import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import {
  isOnboardingDemoFeed,
  isOnboardingDemoSource,
} from '@/components/onboarding/onboardingDemoReport';

import useAuth from '@/composables/useAuth';
import {
  ACTIVE_FEED_STATUS,
  COMPLETED_STATUSES,
  CONNECTION_CHECK_ERROR_STATUS,
  DELETING_FEED_STATUS,
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  RECORD_LIMIT_EXCEEDED_STATUS,
  REQUIRED_FEED_STATUS,
  SOURCE_STATUS_MAP,
  SourceErrorStatus,
  WARNING_STATUSES,
} from '@/constants/data_sources';
import {
  COMPLETE,
  DELETING,
  ERRORED,
  LOCKED,
  NEEDS_PREVIOUS,
  NEEDS_SHEET,
  NOT_SYNCED,
  PENDING,
  PROGRESS,
  StatusType,
  StepType,
} from '@/constants/onboarding';
import { ls } from '@/local_storage';
import {
  useDataSharesStore,
  useFeedsStore,
  useFileUploadsStore,
  usePartnersStore,
  usePopulationsStore,
  useSourcesStore,
  useTeamStore,
} from '@/stores';
import { Feed } from '@/types/feeds';
import { sortByKey } from '@/utils';

import useSeats from './useSeats';

type Step = {
  status: StatusType;
  isActive: boolean;
  subSteps?: string[];
};

export const lsOnboardingCrmAdminSkip = ref(ls.onboardingCrmAdminSkip.get());
export const activeStep = ref<null | number>(null);

const hasOverlaps = ref(false);

export default function () {
  const route = useRoute();
  const feedsStore = useFeedsStore();
  const sourcesStore = useSourcesStore();
  const fileUploadsStore = useFileUploadsStore();
  const partnersStore = usePartnersStore();
  const teamStore = useTeamStore();
  const populationsStore = usePopulationsStore();

  const { feeds } = storeToRefs(feedsStore);
  const { uploadTables } = storeToRefs(fileUploadsStore);
  const { sources } = storeToRefs(sourcesStore);
  const { populations } = storeToRefs(usePopulationsStore());
  const { proposalsReceived, proposalsSent, partnerOrgs } =
    storeToRefs(partnersStore);
  const { hasScope, currentOrg, currentUser } = useAuth();
  const { atSeatLimit } = useSeats();

  const isLoading = computed(
    () =>
      !partnersStore.ready ||
      !feedsStore.ready ||
      !sourcesStore.ready ||
      !fileUploadsStore.ready ||
      !teamStore.ready ||
      !populationsStore.ready,
  );

  /* Invite partner */
  const canAddPartners = computed(() => hasScope('write:partnerships'));
  const invitePartnerStepStatus = computed(() => {
    if (proposalsSent.value.length || partnerOrgs.value.length) return COMPLETE;
    return canAddPartners.value ? PROGRESS : LOCKED;
  });

  // Select a specific proposal if id is specified,
  // select the user proposal if it exists
  // the last proposal received otherwise
  const request = computed(() => {
    const proposalId = route.query?.proposal_id ?? null;
    const sortedProposals = proposalsReceived.value.sort(
      sortByKey('created_at', 'desc', 'timestamp'),
    );
    if (proposalId) {
      const selectedRequest = sortedProposals.find(
        (proposal) => proposal.id === Number(proposalId),
      );
      if (selectedRequest) return selectedRequest;
    }
    const userProposal = sortedProposals.find(
      (proposal) => proposal.contact?.email === currentUser.value?.email,
    );
    if (userProposal) return userProposal;

    return sortedProposals[0];
  });
  const isUserInvited = computed(() => {
    if (request.value) {
      return request.value.contact?.email === currentUser.value?.email;
    }
    return false;
  });

  const partner = computed(
    () => partnerOrgs.value[0] ?? request.value?.sending_organization ?? null,
  );

  const invitePartnerStep = computed<Step>(() => {
    return {
      isActive:
        activeStep.value === StepType.InvitePartner &&
        invitePartnerStepStatus.value !== COMPLETE,
      status: invitePartnerStepStatus.value,
    };
  });

  /* Import data */
  const canAddDataSources = computed(() => hasScope('write:data-sources'));

  const sourceErrorLookup = new Set<SourceErrorStatus>(SOURCE_STATUS_MAP.error);

  function isSheetsOrFileUpload(feed: Feed) {
    const sheetsOrUploadTypes = new Set([
      FILE_UPLOAD_DATA_SOURCE_TYPE,
      GOOGLE_SHEETS_DATA_SOURCE_TYPE,
    ]);
    return sheetsOrUploadTypes.has(feed.integration.type);
  }

  const userSources = computed(() =>
    sources.value.filter((source) => !isOnboardingDemoSource(source)),
  );

  function getUserSourcesByFeedId(feedId: number) {
    return userSources.value.filter((source) => source.feed_id === feedId);
  }

  // The backend seeds a demo CSV (a file-upload feed whose only source is
  // origin: 'system') to populate the demo report. It must never count as a
  // user-connected data source. We detect it via its sources because a real
  // user CSV upload lives on the same file-upload feed.
  function isDemoFeed(feed: Feed) {
    return isOnboardingDemoFeed(feed, sourcesStore.getSourcesByFeedId(feed.id));
  }

  const userFeeds = computed(() =>
    feeds.value.filter((feed) => !isDemoFeed(feed)),
  );

  function getValidSources(feed: Feed) {
    const sources = getUserSourcesByFeedId(feed.id);
    return sources.filter(
      (source) => !sourceErrorLookup.has(source.status as SourceErrorStatus),
    );
  }

  function hasValidSources(feed: Feed) {
    const sources = getValidSources(feed);
    return sources.length > 0;
  }

  const activeFeedsSortedByIntegration = computed(() => {
    if (feeds.value.length === 1) return feeds.value;
    return feeds.value
      .filter((feed) => {
        const isFeedValid = isFeedActiveOrWarning(feed);
        return isSheetsOrFileUpload(feed)
          ? isFeedValid && hasValidSources(feed)
          : isFeedValid;
      })
      .sort((a, b) => {
        const aIsCRM = isSheetsOrFileUpload(a);
        const bIsCRM = isSheetsOrFileUpload(b);

        if (aIsCRM && !bIsCRM) return -1;
        if (!aIsCRM && bIsCRM) return 1;
        return 0;
      });
  });

  const accumulatedSources = computed(() => {
    return activeFeedsSortedByIntegration.value.reduce((acc, feed) => {
      if (isSheetsOrFileUpload(feed)) {
        return acc + getValidSources(feed).length;
      }
      return acc + 1;
    }, 0);
  });

  const unconnectedDataSourceType = computed(() => {
    const inactiveFeed = feeds.value?.find((feed) => {
      if (isSheetsOrFileUpload(feed))
        return isErroredFeed(feed) || !hasValidSources(feed);

      return feed.status !== ACTIVE_FEED_STATUS || !isFeedSyncComplete(feed);
    });

    return inactiveFeed?.integration.type || null;
  });

  const importDataStepStatus = computed<StatusType>(() => {
    const localFeeds = userFeeds.value;

    // if no feeds present
    if (localFeeds.length === 0) {
      return canAddDataSources.value ? PROGRESS : LOCKED;
    }

    const sourceFeedIds = userSources.value.map((source) => source.feed_id);

    /**
     * Check if at least one feed qualifies as complete
     * If not, we can check the individual feed statuses
     */
    const completeFeedExists = localFeeds.some((feed) => {
      if (feed.integration.type === GOOGLE_SHEETS_DATA_SOURCE_TYPE) {
        const gSheetSources = getUserSourcesByFeedId(feed.id);
        if (gSheetSources.length === 0) return false;
      }

      if (isErroredFeed(feed)) return false;

      if (isFeedActiveOrWarning(feed)) {
        return isSheetsOrFileUpload(feed)
          ? isFeedSyncComplete(feed) && hasValidSources(feed)
          : feed.preview_sync_complete;
      }

      return sourceFeedIds.includes(feed.id);
    });

    if (completeFeedExists) return COMPLETE;

    // localFeeds already excludes the backend-seeded demo CSV, so the first
    // one is the feed the user is actually setting up.
    const currentFeed = localFeeds.at(0);
    if (!currentFeed && canAddDataSources.value) return PROGRESS;
    else if (!currentFeed) return LOCKED;

    /* Check whether they abandoned their CRM connection w/out choosing fields */
    if (
      !currentFeed.preview_sync_complete &&
      currentFeed.status === REQUIRED_FEED_STATUS
    ) {
      return NOT_SYNCED;
    }

    /* Check if their CRM connection is errored */
    if (isErroredFeed(currentFeed)) return ERRORED;

    if (currentFeed.status === DELETING_FEED_STATUS) return DELETING;

    /* Check if they haven't added any sheets for their Google Sheets connection */
    if (currentFeed.integration.type === GOOGLE_SHEETS_DATA_SOURCE_TYPE) {
      const gSheetSources = getUserSourcesByFeedId(currentFeed?.id);
      const connectionNeedsSheet = gSheetSources.length === 0;
      if (connectionNeedsSheet) return NEEDS_SHEET;
      if (!hasValidSources(currentFeed)) return ERRORED;
    }

    /* Check if the initial sync hasn't finished */
    if (!isFeedSyncComplete(currentFeed)) return PENDING;

    /* Check if any of their feeds are active, and have an associated source.
     * If so, then that source can be used for populations; the step is complete */
    const activeCsvFeeds = localFeeds
      .filter(feedByType([FILE_UPLOAD_DATA_SOURCE_TYPE]))
      .filter(isFeedActiveOrWarning);
    const csvsConnected =
      activeCsvFeeds.some((feed) => sourceFeedIds.includes(feed.id)) &&
      uploadTables.value.length > 0;
    const activeGoogleSheetFeeds = localFeeds
      .filter(feedByType([GOOGLE_SHEETS_DATA_SOURCE_TYPE]))
      .filter(isFeedActiveOrWarning);

    const googleSheetsConnected = activeGoogleSheetFeeds.some((feed) =>
      sourceFeedIds.includes(feed.id),
    );
    const activeOtherFeeds = localFeeds
      .filter(
        feedByType(
          [FILE_UPLOAD_DATA_SOURCE_TYPE, GOOGLE_SHEETS_DATA_SOURCE_TYPE],
          false,
        ),
      )
      .filter(isFeedActiveOrWarning);
    const otherFeedsConnected = activeOtherFeeds.some((feed) =>
      sourceFeedIds.includes(feed.id),
    );
    if (googleSheetsConnected || csvsConnected || otherFeedsConnected)
      return COMPLETE;

    /* Otherwise, return default state */
    return canAddDataSources.value ? PROGRESS : LOCKED;
  });

  const numStepsLeft = computed(() => {
    const steps: StatusType[] = [
      invitePartnerStepStatus.value,
      connectDataStepStatus.value,
      shareDataStep.value.status,
    ];
    const finalStepCount = hasOverlaps.value ? 0 : 1;
    return steps.filter((step) => step !== 'complete').length + finalStepCount;
  });

  // Inviting CRM admin
  const canInviteTeammates = computed(() => hasScope('write:members'));
  const showInviteButton = computed(
    () => canInviteTeammates.value && !atSeatLimit.value,
  );
  const crmAdminEmails = computed(() =>
    teamStore.redeemableInvites.map((inv) => inv.email),
  );

  const inviteCrmAdminSkipped = computed(
    () => lsOnboardingCrmAdminSkip.value === currentOrg.value.id,
  );
  function skipInviteCrmAdmin() {
    ls.onboardingCrmAdminSkip.set(currentOrg.value.id);
    lsOnboardingCrmAdminSkip.value = currentOrg.value.id;
  }

  const inviteCrmAdminsStatus = computed<StatusType>(() => {
    if (crmAdminEmails.value.length === 0 && !canInviteTeammates.value)
      return LOCKED;
    if (crmAdminEmails.value.length > 0 || inviteCrmAdminSkipped.value)
      return COMPLETE;
    return PROGRESS;
  });

  const hasCustomersPopulation = computed(() =>
    populations.value.find((pop) => pop.standard_type === 'customers'),
  );

  const defineCustomersStatus = computed<StatusType>(() => {
    if (hasCustomersPopulation.value) return COMPLETE;
    return importDataStepStatus.value === COMPLETE ||
      importDataStepStatus.value === PENDING
      ? PROGRESS
      : NEEDS_PREVIOUS;
  });

  const connectDataSubSteps = computed(() => [
    importDataStepStatus.value,
    inviteCrmAdminsStatus.value,
    defineCustomersStatus.value,
  ]);
  const connectDataStepStatus = computed(() => {
    if (connectDataSubSteps.value.some((status) => status === LOCKED))
      return LOCKED;
    if (defineCustomersStatus.value === COMPLETE) return COMPLETE;
    return PROGRESS;
  });

  const connectDataStep = computed<Step>(() => {
    const status = connectDataStepStatus.value;
    const isActive: boolean = activeStep.value === StepType.ConnectData;
    return {
      isActive,
      status,
      subSteps: connectDataSubSteps.value,
    };
  });

  const { isSharingDataWithPartners } = storeToRefs(useDataSharesStore());
  const shareDataStep = computed<Step>(() => {
    const previousComplete =
      connectDataStepStatus.value === COMPLETE &&
      invitePartnerStepStatus.value === COMPLETE;
    return {
      isActive: activeStep.value === StepType.ShareData,
      status: isSharingDataWithPartners.value
        ? COMPLETE
        : previousComplete
          ? PROGRESS
          : NEEDS_PREVIOUS,
    };
  });

  const uncoverStep = computed<Step>(() => {
    return {
      isActive: activeStep.value === StepType.Uncover,
      status: hasOverlaps.value
        ? COMPLETE
        : isSharingDataWithPartners.value
          ? PROGRESS
          : NEEDS_PREVIOUS,
    };
  });

  const defaultActiveStep = computed(() => {
    // As soon as the "Add data" step is started, we open it by default until it's complete
    if (
      defineCustomersStatus.value !== COMPLETE &&
      (inviteCrmAdminsStatus.value === COMPLETE ||
        (importDataStepStatus.value !== PROGRESS &&
          importDataStepStatus.value !== LOCKED))
    )
      return StepType.ConnectData;
    // If "Add data" isn't started, we open by default "Invite partner"
    if (invitePartnerStepStatus.value !== COMPLETE)
      return StepType.InvitePartner;
    if (defineCustomersStatus.value !== COMPLETE) return StepType.ConnectData;
    if (!isSharingDataWithPartners.value) return StepType.ShareData;
    return StepType.Uncover;
  });

  /* Helper functions 🤝 */
  function handleChangeActiveStep(type: StepType) {
    activeStep.value = type;
  }

  // Set active step to the default step if stores are ready or if the "default" changes
  watch(
    [isLoading, defaultActiveStep],
    ([loading, defaultActiveStep]: [boolean, StepType]) => {
      if (!loading && activeStep.value !== defaultActiveStep) {
        activeStep.value = defaultActiveStep;
      }
    },
  );

  return {
    isLoading,
    isUserInvited,
    invitePartnerStep,
    invitePartnerStepStatus,
    request,
    partner,
    crmAdminEmails,
    inviteCrmAdminsStatus,
    importDataStepStatus,
    connectDataStep,
    shareDataStep,
    uncoverStep,
    inviteCrmAdminSkipped,
    defineCustomersStatus,
    unconnectedDataSourceType,
    showInviteButton,
    activeFeedsSortedByIntegration,
    accumulatedSources,
    hasCustomersPopulation,
    numStepsLeft,
    hasOverlaps,
    handleChangeActiveStep,
    skipInviteCrmAdmin,
  };
}

/* Helper functions 🤝 */
const feedByType =
  (types: string[], inclusive = true) =>
  (feed: Feed) => {
    return inclusive
      ? types.includes(feed.integration.type)
      : !types.includes(feed.integration.type);
  };

function isFeedSyncComplete(feed: Feed) {
  return feed.preview_sync_complete || feed.initial_sync_complete;
}

export function isFeedActiveOrWarning(feed: Feed) {
  return [...COMPLETED_STATUSES, ...WARNING_STATUSES].includes(feed.status);
}

export function __resetNewOnboarding() {
  activeStep.value = null;
  lsOnboardingCrmAdminSkip.value = null;
  hasOverlaps.value = false;
}

function isErroredFeed(feed: Feed) {
  return [
    CONNECTION_CHECK_ERROR_STATUS,
    RECORD_LIMIT_EXCEEDED_STATUS,
    REQUIRED_FEED_STATUS,
    DELETING_FEED_STATUS,
  ].includes(feed.status);
}
